import { atom, useAtom } from 'jotai'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import CloudDoneTwoToneIcon from '@mui/icons-material/CloudDoneTwoTone'

import { FormControlWithLabelWrapper } from '@/views/wizard/steps/wizard-common'
import Shelly from './shelly'
import Tuya from './tuya'
import { useApplianceControls } from '@/hooks/use-appliance-control'
import { ApplianceControlType } from '@/types'
import TuyaTest from './tuya/test-button'
import CustomButton from '@/components/custom-button'
import DestroyApplianceControlForm from '@/components/forms/appliance-control/destroy'
import ApplianceLoader from '@/wrappers/appliance-loader'
import ApplianceHdoDependencyToggle from '../appliance-hdo-dependency-toggle'

type SOCKET_IDS = 'tuya' | 'shelly'

const SOCKETS: {
    id: SOCKET_IDS,
    name: string,
}[] = [
        { id: 'tuya', name: 'Tuya' },
        { id: 'shelly', name: 'Shelly' },
    ]

const APPLIANCE_CONTROL_TYPE_MAP = {
    [ApplianceControlType.tuya]: 'Tuya',
    [ApplianceControlType.shelly]: 'Shelly',
}

const socketIdAtom = atom<SOCKET_IDS>('tuya')
const connectNewAtom = atom<boolean>(false)

export default function SocketWizard({
    applianceId,
    successCallback,
    showHdoDependencyToggle,
}: {
    applianceId: number
    successCallback: (arg1: boolean) => void,
    showHdoDependencyToggle?: boolean,
}) {

    const [socketId, setSocketId] = useAtom(socketIdAtom)
    const [connectNew, setConnectNew] = useAtom(connectNewAtom)

    const { status, data, error } = useApplianceControls(applianceId)
    
    if (status === 'pending') {
        return <Box sx={{
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
            <CircularProgress />
        </Box>
    }
    
    if (error) {
        return <Box sx={{
            py: 2,
        }}>
            <Alert severity="error" sx={{ mt: 2 }}>
                Nepovedlo se načíst propojené zásuvky:
                {error.message}
            </Alert>
        </Box>
    }

    return (
        <Box>
            {data && data.length > 0 && !connectNew &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                }}>
                    <CloudDoneTwoToneIcon color='success' fontSize="large"/>
                    <Typography variant='h6' component="p" textAlign="center">
                        Zdá se, že již máte nastavené spojení s <b>{APPLIANCE_CONTROL_TYPE_MAP[data[0].type]}</b> zásuvkou.
                    </Typography>
                    <Box>
                        <TuyaTest applianceId={applianceId} successCallback={successCallback} />
                    </Box>
                    <Box>
                        <CustomButton
                            variant="outlined"
                            color="primary"
                            text="Připojit jinou zásuvku"
                            onClick={() => setConnectNew(true)}
                        />
                    </Box>
                    <DestroyApplianceControlForm applianceId={applianceId} applianceControlId={data[0].id} />
                    {showHdoDependencyToggle &&
                        <ApplianceLoader applianceId={applianceId}>
                            {appliance => (
                                <ApplianceHdoDependencyToggle
                                    applianceId={applianceId}
                                    isDependentOnHdo={appliance.is_dependent_on_hdo}
                                />
                            )}
                        </ApplianceLoader>
                    }
                </Box>
            }
            {((data && data.length === 0) || connectNew ) &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}>
                    {connectNew &&
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                        }}>
                            <CustomButton
                                variant="outlined"
                                color="primary"
                                text="Zrušit připojení jiné zásuvky"
                                onClick={() => setConnectNew(false)}
                            />
                        </Box>
                    }
                    <FormControlWithLabelWrapper
                        label={'Vyberte výrobce zásuvky'}
                        labelId='socket-label-id'
                    >
                        <Select
                            id="socket-label-id"
                            value={socketId}
                            onChange={event => {
                                const newId = event.target.value as SOCKET_IDS
                                setSocketId(newId)
                            }}
                        >
                            {SOCKETS.map(({ name, id }) => (
                                <MenuItem key={id} value={id}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControlWithLabelWrapper>

                    {socketId === 'shelly' ?
                        <Shelly
                            applianceId={applianceId}
                            successCallback={successCallback}
                        />
                        :
                        <Tuya
                            applianceId={applianceId}
                            successCallback={successCallback}
                        />
                    }
                </Box>
            }
        </Box>
    )
}
